import {BSKY_SERVICE} from '#/lib/constants'

/**
 * The address the PDS serves one blob from.
 *
 * Named for the PDS rather than for documents because a document is no longer
 * the only blob in this feature: a page's audio is a blob too, uploaded the same
 * way, and both are fetched from this one endpoint. Anything that knows a DID and
 * a CID can build its own address from it — see
 * `#/lib/document/materialPublish` for the clip case.
 */
export function buildPdsBlobUrl(did: string, cid: string): string {
  return `${BSKY_SERVICE}/xrpc/com.atproto.sync.getBlob?did=${encodeURIComponent(
    did,
  )}&cid=${encodeURIComponent(cid)}`
}

/**
 * Blob URL for a document, served by the PDS itself. The raw BlobRef is what
 * the feed returns, so this is the only fetchable address for a document.
 */
export const buildDocumentBlobUrl = buildPdsBlobUrl

export function documentTypeLabel(mimeType: string): string {
  switch (mimeType) {
    case 'application/pdf':
      return 'PDF'
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'Word'
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'PowerPoint'
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'Excel'
    case 'text/markdown':
      return 'Markdown'
    default:
      return 'Document'
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Documents are revealed page-by-page, never as a downloadable file, so we
 * never render a direct link to the blob. This mirrors the anti-scraping
 * policy in the server's document upload guide.
 */
export function isDocumentPreviewable(mimeType: string): boolean {
  return mimeType === 'application/pdf'
}
